import { Controller } from "@hotwired/stimulus";

/**
 * Floats a clone of the matrix table's <thead> at the top of the viewport once
 * the real thead scrolls past it. The original thead lives inside an
 * overflow-x-auto wrapper, which traps `position: sticky` — so a fixed-position
 * mirror is the only way to get viewport-anchored sticky behavior while
 * preserving horizontal scroll inside the wrapper.
 */
export default class extends Controller {
  static targets = ["scroll", "table"];

  declare scrollTarget: HTMLElement;
  declare tableTarget: HTMLTableElement;

  private mq = window.matchMedia("(min-width: 768px)");
  private overlay: HTMLDivElement | null = null;
  private overlayTable: HTMLTableElement | null = null;
  private overlayCorner: HTMLElement | null = null;
  private originalThead: HTMLTableSectionElement | null = null;
  private rafId: number | null = null;

  connect() {
    this.originalThead = this.tableTarget.tHead;
    if (!this.originalThead) return;

    if (this.mq.matches) this.enable();
    this.mq.addEventListener("change", this.onBreakpointChange);
  }

  disconnect() {
    this.mq.removeEventListener("change", this.onBreakpointChange);
    this.disable();
  }

  private onBreakpointChange = (e: MediaQueryListEvent) => {
    if (e.matches) this.enable();
    else this.disable();
  };

  private enable() {
    if (this.overlay) return;
    this.buildOverlay();
    window.addEventListener("scroll", this.requestUpdate, { passive: true });
    window.addEventListener("resize", this.requestUpdate);
    this.scrollTarget.addEventListener("scroll", this.syncHorizontal, { passive: true });
    this.update();
  }

  private disable() {
    window.removeEventListener("scroll", this.requestUpdate);
    window.removeEventListener("resize", this.requestUpdate);
    this.scrollTarget.removeEventListener("scroll", this.syncHorizontal);
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    this.overlay?.remove();
    this.overlay = null;
    this.overlayTable = null;
    this.overlayCorner = null;
  }

  private buildOverlay() {
    this.overlay = document.createElement("div");
    this.overlay.style.cssText = [
      "position: fixed",
      "top: 0",
      "left: 0",
      "overflow: hidden",
      "z-index: 40",
      "pointer-events: none",
      "display: none",
      // Match wrapper visual styling so the overlay reads as the same surface.
      "background-color: var(--color-base-100)",
    ].join(";");

    this.overlayTable = document.createElement("table");
    this.overlayTable.className = this.tableTarget.className;
    this.overlayTable.style.cssText = [
      "pointer-events: auto",
      "will-change: transform",
      "margin: 0",
    ].join(";");

    const clonedThead = this.originalThead!.cloneNode(true) as HTMLTableSectionElement;
    // Strip IDs from the clone to avoid duplicate-ID collisions in the DOM.
    clonedThead.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));
    // The cloned corner cell uses `position: sticky` in the original, but
    // sticky doesn't react to transforms — we move the overlay table via
    // translateX. Counter-translate the corner cell in syncHorizontal so it
    // stays pinned left, mirroring the body's sticky model column.
    this.overlayCorner = clonedThead.querySelector(".matrix-corner") as HTMLElement | null;
    if (this.overlayCorner) {
      this.overlayCorner.style.position = "relative";
      this.overlayCorner.style.zIndex = "1";
    }
    this.overlayTable.appendChild(clonedThead);
    this.overlay.appendChild(this.overlayTable);
    document.body.appendChild(this.overlay);
  }

  private requestUpdate = () => {
    if (this.rafId !== null) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.update();
    });
  };

  private update() {
    if (!this.overlay || !this.overlayTable || !this.originalThead) return;

    const wrapperRect = this.scrollTarget.getBoundingClientRect();
    const theadRect = this.originalThead.getBoundingClientRect();

    // Show once the real thead has scrolled past the viewport top, and hide
    // again once the wrapper itself is leaving the viewport.
    const shouldShow = theadRect.top < 0 && wrapperRect.bottom > theadRect.height;

    if (!shouldShow) {
      this.overlay.style.display = "none";
      return;
    }

    this.overlay.style.display = "block";
    this.overlay.style.left = `${wrapperRect.left}px`;
    this.overlay.style.width = `${wrapperRect.width}px`;
    this.overlayTable.style.width = `${this.tableTarget.offsetWidth}px`;
    this.syncHorizontal();
  }

  private syncHorizontal = () => {
    if (!this.overlayTable || this.overlay?.style.display === "none") return;
    const scrollLeft = this.scrollTarget.scrollLeft;
    this.overlayTable.style.transform = `translateX(${-scrollLeft}px)`;
    if (this.overlayCorner) {
      this.overlayCorner.style.transform = `translateX(${scrollLeft}px)`;
    }
  };
}
